import { useState, useCallback, type ReactNode } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { SectionHeader, ErrorMessage, ConfirmDialog, InfoHint, Panel, SavedCheck } from "../components/ui";
import { Badge, Button, Field, Input } from "@winche/ui";
import { useFlash } from "../lib/hooks";
import { type OrModel } from "../components/ModelSearch";
import { ModelSlot } from "../components/ModelSlot";
import { ModelChangeDialog, ModelChangeNotice } from "../components/ModelChangeDialog";
import type { ModelChange } from "../lib/model-change";
import { ReasoningSelect } from "../components/ReasoningSelect";
import { callableFailure, describeCallableError, type AdminFailure } from "../lib/callable-error";
import { useAnnouncementBannerForm } from "../lib/announcement-banner-form";
import { isFreeModelId } from "../lib/or-models";
import {
  announcementBannerProblems,
  canSaveAnnouncementBanner,
  effectiveFallbackModelId,
  normalizeAnnouncementBanner,
  type AnnouncementBanner,
  type ModelReasoningSetting,
} from "@winche/shared";


const setFreeForAllModeFn = httpsCallable<{ enabled: boolean }, { success: boolean; freeForAllMode: boolean }>(
  functions,
  "setFreeForAllMode",
);
const setLsTestModeFn = httpsCallable<{ enabled: boolean }, { success: boolean; lsTestMode: boolean }>(
  functions,
  "setLsTestMode",
);
const reconcileMonetizationFn = httpsCallable<Record<string, never>, { success: boolean; skipped?: boolean }>(
  functions,
  "reconcileMonetization",
);
const reconcileTokenUsageMapsFn = httpsCallable<{ dryRun?: boolean }, {
  dryRun: boolean;
  docsScanned: number;
  docsNeedingWrite: number;
  dottedKeysDeleted: number;
  written: number;
}>(
  functions,
  "reconcileTokenUsageMaps",
);

interface Props {
  freeForAll: boolean | null;
  lsTestMode: boolean | null;
  /** undefined until config/global answers; null when it holds no banner. */
  announcementBanner: AnnouncementBanner | null | undefined;
  activeModelId: string | null;
  activeVisionModelId: string | null;
  paidFallbackModelId: string | null;
  paidFallbackVisionModelId: string | null;
  useFreeTextModel: boolean | null;
  useFreeVisionModel: boolean | null;
  reasoningEffortByModel: Record<string, string> | null;
  /** `acknowledgeFreeTierRetention`: the operator confirmed the free-tier warning (ux-admin-02). */
  onSetActiveModel: (modelId: string, acknowledgeFreeTierRetention?: boolean) => Promise<void>;
  onSetActiveVisionModel: (modelId: string, acknowledgeFreeTierRetention?: boolean) => Promise<void>;
  onSetPaidFallbackModel: (modelId: string | null) => Promise<void>;
  onSetPaidFallbackVisionModel: (modelId: string | null) => Promise<void>;
  /** `acknowledgeFreeTierRetention`: turning it on over a free-tier model needs the same confirmation. */
  onSetUseFreeTextModel: (enabled: boolean, acknowledgeFreeTierRetention?: boolean) => Promise<void>;
  onSetUseFreeVisionModel: (enabled: boolean, acknowledgeFreeTierRetention?: boolean) => Promise<void>;
  onSetModelReasoning: (modelId: string, effort: ModelReasoningSetting) => Promise<void>;
  onSetAnnouncementBanner: (data: AnnouncementBanner) => Promise<void>;
}

/**
 * Switch colored by what ON means (admin-01): `risky` switches turn amber when
 * on (free-for-all, test payments), healthy ones green. Named by `label`.
 */
function Toggle({
  enabled,
  disabled,
  onToggle,
  label,
  risky = false,
}: {
  enabled: boolean;
  disabled: boolean;
  onToggle: () => void;
  label: string;
  risky?: boolean;
}) {
  const onColor = risky ? "var(--warning-solid)" : "var(--success-solid)";
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={label}
      onClick={onToggle}
      disabled={disabled}
      className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-150 disabled:cursor-not-allowed disabled:bg-disabled"
      style={disabled ? undefined : { backgroundColor: enabled ? onColor : "var(--border-strong)" }}
    >
      <span
        className="inline-block h-[18px] w-[18px] rounded-full bg-white shadow-xs transition-transform duration-150"
        style={{ transform: enabled ? "translateX(22px)" : "translateX(3px)" }}
      />
    </button>
  );
}


/** «EN text» trimmed for one line of the live summary. */
function quoted(text: string): string {
  const t = text.trim();
  return t ? `«${t.length > 80 ? `${t.slice(0, 79)}…` : t}»` : "(empty)";
}

/**
 * The global announcement banner (ux-admin-03, ux-admin-08). The form follows
 * config/global while it has no unsaved changes, so reloading on #settings
 * fills it when the snapshot lands instead of showing Off and empty; Save
 * waits for that snapshot. What production shows ("Live") is kept apart from
 * the draft, and a banner that is on needs both texts.
 */
function AnnouncementBannerCard({
  live,
  onSave,
}: {
  live: AnnouncementBanner | null | undefined;
  onSave: (banner: AnnouncementBanner) => Promise<void>;
}) {
  const [form, dispatch] = useAnnouncementBannerForm(live);

  const [saving, setSaving] = useState(false);
  const [saved, flashSaved] = useFlash();
  const [error, setError] = useState<AdminFailure | null>(null);
  const loaded = form.live !== undefined;
  const problems = announcementBannerProblems(form.draft);

  async function handleSave() {
    const banner = normalizeAnnouncementBanner(form.draft);
    setSaving(true);
    setError(null);
    try {
      await onSave(banner);
      dispatch({ type: "saved", banner });
      flashSaved();
    } catch (err) {
      setError(describeCallableError("setAnnouncementBanner", err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Panel className="p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold">Announcement banner</h3>
            {!loaded ? (
              <Badge tone="neutral" size="sm">
                Loading
              </Badge>
            ) : (
              <Badge tone={form.live?.enabled ? "brand" : "neutral"} size="sm">
                {form.live?.enabled ? "Live: On" : "Live: Off"}
              </Badge>
            )}
            {form.dirty && (
              <Badge tone="warning" size="sm">
                Unsaved changes
              </Badge>
            )}
          </div>
          <p className="text-label text-muted-foreground">
            A top banner across the whole site for launches, incidents or important notices.
          </p>
        </div>
        <Toggle
          enabled={form.draft.enabled}
          disabled={saving || !loaded}
          onToggle={() => dispatch({ type: "edit", patch: { enabled: !form.draft.enabled } })}
          label="Show the announcement banner"
        />
      </div>

      <p className="mt-3 rounded-md bg-sunken px-3 py-2 text-caption text-muted-foreground" aria-live="polite">
        {!loaded
          ? "Live: loading config/global…"
          : form.live?.enabled
            ? `Live: On · EN ${quoted(form.live.textEn)} · ES ${quoted(form.live.textEs)}`
            : "Live: Off"}
      </p>

      <div className="mt-4 space-y-3">
        <Field
          label="Message (EN)"
          error={problems.includes("text_en_required") ? "Required while the banner is on." : undefined}
        >
          <Input
            id="banner-text-en"
            type="text"
            value={form.draft.textEn}
            disabled={!loaded}
            onChange={(e) => dispatch({ type: "edit", patch: { textEn: e.target.value } })}
            placeholder="e.g. We're experiencing high demand — some requests may be slower than usual."
          />
        </Field>
        <Field
          label="Message (ES)"
          error={problems.includes("text_es_required") ? "Required while the banner is on." : undefined}
        >
          <Input
            id="banner-text-es"
            type="text"
            value={form.draft.textEs}
            disabled={!loaded}
            onChange={(e) => dispatch({ type: "edit", patch: { textEs: e.target.value } })}
            placeholder="ej. Estamos experimentando alta demanda — algunas peticiones pueden demorar."
          />
        </Field>
        <p className="text-caption text-muted-foreground">Spanish UI shows ES; every other language shows EN.</p>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Button size="sm" onClick={() => void handleSave()} loading={saving} disabled={!canSaveAnnouncementBanner(form)} className="min-w-28">
          Save banner
        </Button>
        <SavedCheck visible={saved} />
      </div>
      {error && (
        <div className="mt-3">
          <ErrorMessage failure={error} />
        </div>
      )}
    </Panel>
  );
}

export function SettingsSection({ freeForAll, lsTestMode, announcementBanner, activeModelId, activeVisionModelId, paidFallbackModelId, paidFallbackVisionModelId, useFreeTextModel, useFreeVisionModel, reasoningEffortByModel, onSetActiveModel, onSetActiveVisionModel, onSetPaidFallbackModel, onSetPaidFallbackVisionModel, onSetUseFreeTextModel, onSetUseFreeVisionModel, onSetModelReasoning, onSetAnnouncementBanner }: Props) {
  const [toggling, setToggling] = useState(false);
  /** Production write waiting for confirmation (admin-01). */
  const [confirm, setConfirm] = useState<null | "ffa" | "ls" | "maps" | "reconcile">(null);
  const [error, setError] = useState<AdminFailure | null>(null);
  const [localOverride, setLocalOverride] = useState<boolean | null>(null);

  const [lsToggling, setLsToggling] = useState(false);
  const [lsError, setLsError] = useState<AdminFailure | null>(null);
  const [lsLocalOverride, setLsLocalOverride] = useState<boolean | null>(null);

  type PickerSlot = "free-text" | "free-vision" | "fallback-text" | "fallback-vision";
  const [openPicker, setOpenPicker] = useState<PickerSlot | null>(null);
  const [savingModel, setSavingModel] = useState<PickerSlot | null>(null);
  const [modelError, setModelError] = useState<AdminFailure | null>(null);

  // Free-tier toggles (text / vision): they only change a `:free` model. ON =
  // the free variant runs and fails over to the fallback; OFF = it is
  // swapped for its paid model. A paid model (Haiku, the default) ignores them.
  const [freeTierOverride, setFreeTierOverride] = useState<Record<"text" | "vision", boolean | null>>({ text: null, vision: null });
  const [freeTierToggling, setFreeTierToggling] = useState<Record<"text" | "vision", boolean>>({ text: false, vision: false });
  const [freeTierError, setFreeTierError] = useState<AdminFailure | null>(null);

  const effectiveFreeTier = (kind: "text" | "vision"): boolean | null => {
    const value = kind === "text" ? useFreeTextModel : useFreeVisionModel;
    return freeTierOverride[kind] ?? value;
  };

  /** Writes the free-tier toggle (optimistic); true when it saved. */
  async function handleFreeTierToggle(kind: "text" | "vision", next: boolean, acknowledgeFreeTierRetention: boolean): Promise<boolean> {
    setFreeTierError(null);
    setFreeTierToggling(t => ({ ...t, [kind]: true }));
    setFreeTierOverride(o => ({ ...o, [kind]: next })); // optimistic
    try {
      if (kind === "text")   await onSetUseFreeTextModel(next, acknowledgeFreeTierRetention);
      if (kind === "vision") await onSetUseFreeVisionModel(next, acknowledgeFreeTierRetention);
      return true;
    } catch (err) {
      setFreeTierOverride(o => ({ ...o, [kind]: null })); // revert on error
      setFreeTierError(describeCallableError("setUseFreeModel", err));
      return false;
    } finally {
      setFreeTierToggling(t => ({ ...t, [kind]: false }));
    }
  }

  // Every model write moves production AI calls: it waits for a confirmation
  // with its scope, and the last one can be reverted (ux-admin-02).
  const [pendingChange, setPendingChange] = useState<ModelChange | null>(null);
  const [lastChange, setLastChange] = useState<ModelChange | null>(null);

  const activeOf = (kind: "text" | "vision") => (kind === "text" ? activeModelId : activeVisionModelId);
  const fallbackOf = (kind: "text" | "vision") => (kind === "text" ? paidFallbackModelId : paidFallbackVisionModelId);

  function handlePickFreeModel(kind: "text" | "vision", m: OrModel) {
    setOpenPicker(null);
    setPendingChange({ type: "active", kind, from: activeOf(kind), to: m.id });
  }

  function handleClearFreeModel(kind: "text" | "vision") {
    const active = activeOf(kind);
    const base = active?.replace(/:free$/, "") ?? "";
    if (base) setPendingChange({ type: "active", kind, from: active, to: base });
  }

  function handlePickFallback(kind: "text" | "vision", m: OrModel) {
    setOpenPicker(null);
    setPendingChange({ type: "fallback", kind, from: fallbackOf(kind), to: m.id });
  }

  function handleClearFallback(kind: "text" | "vision") {
    setPendingChange({ type: "fallback", kind, from: fallbackOf(kind), to: null });
  }

  /** The confirmed change, written through its callable. A failure shows under the models. */
  async function applyModelChange(change: ModelChange, acknowledgeFreeTierRetention: boolean) {
    if (change.type === "freeTier") {
      if (await handleFreeTierToggle(change.kind, change.to, acknowledgeFreeTierRetention)) setLastChange(change);
      return;
    }
    const slot: PickerSlot = `${change.type === "active" ? "free" : "fallback"}-${change.kind}`;
    setSavingModel(slot);
    setModelError(null);
    try {
      if (change.type === "active") {
        if (change.kind === "text") await onSetActiveModel(change.to, acknowledgeFreeTierRetention);
        else await onSetActiveVisionModel(change.to, acknowledgeFreeTierRetention);
      } else if (change.kind === "text") {
        await onSetPaidFallbackModel(change.to);
      } else {
        await onSetPaidFallbackVisionModel(change.to);
      }
      setLastChange(change);
    } catch (e) {
      const callable =
        change.type === "fallback" ? "setPaidFallbackModel" : change.kind === "text" ? "setActiveModel" : "setActiveVisionModel";
      setModelError(describeCallableError(callable, e));
    } finally {
      setSavingModel(null);
    }
  }

  const [reconciling, setReconciling] = useState(false);
  const [reconcileMsg, setReconcileMsg] = useState<string | null>(null);
  const [reconcileError, setReconcileError] = useState<AdminFailure | null>(null);
  const [reconcilingMaps, setReconcilingMaps] = useState(false);
  const [mapsMsg, setMapsMsg] = useState<string | null>(null);
  const [mapsError, setMapsError] = useState<AdminFailure | null>(null);

  // Reasoning per model (ux-admin-09): a failed write says so next to the selects.
  const [savingReasoning, setSavingReasoning] = useState<string | null>(null);
  const [reasoningError, setReasoningError] = useState<AdminFailure | null>(null);

  async function handleReasoningChange(modelId: string, effort: ModelReasoningSetting) {
    setSavingReasoning(modelId);
    setReasoningError(null);
    setReasoningError(await callableFailure("setModelReasoningEffort", () => onSetModelReasoning(modelId, effort)));
    setSavingReasoning(null);
  }

  const handleReconcile = useCallback(async () => {
    setReconciling(true);
    setReconcileMsg(null);
    setReconcileError(null);
    try {
      const res = await reconcileMonetizationFn({});
      setReconcileMsg(res.data.skipped ? "Already ran today — skipped." : "Done — Overview updated.");
    } catch (err) {
      setReconcileError(describeCallableError("reconcileMonetization", err));
    } finally {
      setReconciling(false);
    }
  }, []);

  const handleReconcileMaps = useCallback(async (dryRun: boolean) => {
    setReconcilingMaps(true);
    setMapsMsg(null);
    setMapsError(null);
    try {
      const res = await reconcileTokenUsageMapsFn({ dryRun });
      const d = res.data;
      setMapsMsg(
        `${d.dryRun ? "Dry-run" : "Wrote"}: scanned ${d.docsScanned}, ${d.docsNeedingWrite} with dotted keys, ${d.dottedKeysDeleted} keys, written ${d.written}.`,
      );
    } catch (err) {
      setMapsError(describeCallableError("reconcileTokenUsageMaps", err));
    } finally {
      setReconcilingMaps(false);
    }
  }, []);

  const effectiveValue = localOverride ?? freeForAll;

  const handleToggle = useCallback(async () => {
    if (effectiveValue === null) return;
    const next = !effectiveValue;
    setError(null);
    setToggling(true);
    setLocalOverride(next); // optimistic
    try {
      await setFreeForAllModeFn({ enabled: next });
    } catch (err) {
      setLocalOverride(null); // revert on error
      setError(describeCallableError("setFreeForAllMode", err));
    } finally {
      setToggling(false);
    }
  }, [effectiveValue]);

  const effectiveLsTestMode = lsLocalOverride ?? lsTestMode;

  const handleLsToggle = useCallback(async () => {
    if (effectiveLsTestMode === null) return;
    const next = !effectiveLsTestMode;
    setLsError(null);
    setLsToggling(true);
    setLsLocalOverride(next);
    try {
      await setLsTestModeFn({ enabled: next });
    } catch (err) {
      setLsLocalOverride(null);
      setLsError(describeCallableError("setLsTestMode", err));
    } finally {
      setLsToggling(false);
    }
  }, [effectiveLsTestMode]);


  const settingCard = (title: string, badge: ReactNode, description: ReactNode, control: ReactNode, footer?: ReactNode) => (
    <Panel className="p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold">{title}</h3>
            {badge}
          </div>
          <div className="text-label text-muted-foreground">{description}</div>
        </div>
        {control}
      </div>
      {footer}
    </Panel>
  );

  return (
    <div>
      <SectionHeader title="Settings" subtitle="Models, feature flags and maintenance jobs." />

      <div className="max-w-2xl space-y-4">
        <Panel className="p-5">
          <div className="mb-1 flex items-center gap-1.5">
            <h2 className="text-sm font-semibold">AI models</h2>
            <InfoHint text="Text and vision calls run on these models (Haiku 5.5 by default); a call that fails moves to the fallback. evaluate_job has its own model, and every Anthropic call shares the daily Haiku cap (Overview)." />
          </div>
          <p className="mb-5 text-label text-muted-foreground">
            Which models run every AI operation except evaluate_job. Compare costs in Benchmark; today&apos;s Haiku budget is on
            Overview.
          </p>

          <div className="space-y-6">
            {(["text", "vision"] as const).map((kind) => {
              const activeId = kind === "text" ? activeModelId : activeVisionModelId;
              const fallbackId = kind === "text" ? paidFallbackModelId : paidFallbackVisionModelId;
              const isFree = activeId ? isFreeModelId(activeId) : false;
              const hasPaidBase = isFree && activeId ? activeId.endsWith(":free") : false;
              const freeSlot: PickerSlot = `free-${kind}`;
              const fallSlot: PickerSlot = `fallback-${kind}`;
              const freeTier = effectiveFreeTier(kind);
              const effectiveFallback = effectiveFallbackModelId(activeId, fallbackId);
              const freeTierHint = !isFree
                ? `No effect now: ${activeId ?? "the model"} is not a :free model.`
                : freeTier
                  ? "The :free model runs on OpenRouter's free quota and moves to the fallback on 429, 404 or timeout."
                  : "The :free model is swapped for its paid fallback on every call.";
              return (
                <section key={kind} aria-labelledby={`models-${kind}`} className={kind === "vision" ? "border-t border-border pt-5" : undefined}>
                  <h3 id={`models-${kind}`} className="mb-3 text-label font-semibold">
                    {kind === "text" ? "Text" : "Vision"}
                  </h3>

                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="mb-0.5 flex items-center gap-2">
                        <p className="text-label font-medium" id={`free-tier-${kind}`}>
                          Use free tier
                        </p>
                        {freeTier !== null && (
                          <Badge tone={freeTier ? "success" : "neutral"} size="sm">
                            {freeTier ? "On" : "Off"}
                          </Badge>
                        )}
                      </div>
                      <p className="text-caption text-muted-foreground">{freeTierHint}</p>
                    </div>
                    <Toggle
                      enabled={freeTier ?? false}
                      disabled={freeTierToggling[kind] || freeTier === null}
                      onToggle={() =>
                        freeTier !== null &&
                        setPendingChange({ type: "freeTier", kind, model: activeId, from: freeTier, to: !freeTier })
                      }
                      label={`Use free tier (${kind})`}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-x-3 gap-y-2 @lg:grid-cols-[8rem_1fr] @lg:items-center">
                    <label htmlFor={`slot-${freeSlot}`} className="text-label text-muted-foreground">
                      Free model
                    </label>
                    <ModelSlot
                      id={`slot-${freeSlot}`}
                      label={`Free ${kind} model`}
                      value={isFree ? activeId : null}
                      placeholder="None — paid model directly"
                      open={openPicker === freeSlot}
                      saving={savingModel === freeSlot}
                      highlight={isFree}
                      onOpenChange={(o) => setOpenPicker(o ? freeSlot : null)}
                      onSelect={(m) => handlePickFreeModel(kind, m)}
                      onClear={hasPaidBase ? () => handleClearFreeModel(kind) : undefined}
                      clearLabel={`Stop using a free ${kind} model`}
                      search={{ freeOnly: true, visionOnly: kind === "vision" }}
                    />

                    <label htmlFor={`reasoning-${kind}`} className="text-label text-muted-foreground">
                      Reasoning
                    </label>
                    <ReasoningSelect
                      id={`reasoning-${kind}`}
                      modelId={activeId}
                      effortByModel={reasoningEffortByModel}
                      disabled={!activeId || savingModel !== null || savingReasoning !== null}
                      onChange={(id, effort) => void handleReasoningChange(id, effort)}
                    />

                    <label htmlFor={`slot-${fallSlot}`} className="text-label text-muted-foreground">
                      Paid fallback
                    </label>
                    <ModelSlot
                      id={`slot-${fallSlot}`}
                      label={`Paid ${kind} fallback`}
                      value={fallbackId}
                      placeholder={effectiveFallback ? `None set: falls back to ${effectiveFallback}` : "None: no fallback"}
                      open={openPicker === fallSlot}
                      saving={savingModel === fallSlot}
                      onOpenChange={(o) => setOpenPicker(o ? fallSlot : null)}
                      onSelect={(m) => handlePickFallback(kind, m)}
                      onClear={fallbackId ? () => handleClearFallback(kind) : undefined}
                      clearLabel={`Remove the paid ${kind} fallback`}
                      search={{ paidOnly: true, visionOnly: kind === "vision" }}
                    />

                    {fallbackId && (
                      <>
                        <label htmlFor={`fallback-reasoning-${kind}`} className="text-label text-muted-foreground">
                          Fallback reasoning
                        </label>
                        <ReasoningSelect
                          id={`fallback-reasoning-${kind}`}
                          modelId={fallbackId}
                          effortByModel={reasoningEffortByModel}
                          disabled={savingModel !== null || savingReasoning !== null}
                          onChange={(id, effort) => void handleReasoningChange(id, effort)}
                        />
                      </>
                    )}
                  </div>
                </section>
              );
            })}
          </div>

          <details className="mt-5 text-caption text-muted-foreground">
            <summary className="inline-flex min-h-11 cursor-pointer items-center text-label text-foreground md:pointer-fine:min-h-0">
              How reasoning works
            </summary>
            <p className="mt-1">
              Reasoning is set per model. Gemini Flash Lite and MiniMax M3 default to Off (extra thinking made CV generation hang).
              Auto follows the operation: evaluation and CV use low, interview prep uses medium.
            </p>
          </details>

          {lastChange && (
            <ModelChangeNotice change={lastChange} onRevert={setPendingChange} disabled={savingModel !== null || pendingChange !== null} />
          )}
          {modelError && (
            <div className="mt-4">
              <ErrorMessage failure={modelError} />
            </div>
          )}
          {freeTierError && (
            <div className="mt-4">
              <ErrorMessage failure={freeTierError} />
            </div>
          )}
          {reasoningError && (
            <div className="mt-4">
              <ErrorMessage failure={reasoningError} />
            </div>
          )}
        </Panel>

        <AnnouncementBannerCard live={announcementBanner} onSave={onSetAnnouncementBanner} />

        <div className="pt-4">
          <h2 className="text-label font-semibold text-danger-fg">Danger zone</h2>
          <p className="text-caption text-muted-foreground">Switches and writes that change production for every user. Each one asks for confirmation.</p>
        </div>

        {settingCard(
          "Free-for-all mode",
          effectiveValue !== null ? (
            <Badge tone={effectiveValue ? "warning" : "neutral"} size="sm" dot={effectiveValue}>
              {effectiveValue ? "On" : "Off"}
            </Badge>
          ) : null,
          "Bypasses plan limits for every user (PRO-level access), for launches or demos. The Haiku daily cap still applies: past it, background AI work waits for the next UTC day, so more usage means a longer queue. Turn it off within 72 hours.",
          <Toggle enabled={effectiveValue ?? false} disabled={toggling || freeForAll === null} onToggle={() => setConfirm("ffa")} label="Free-for-all mode" risky />,
          error ? (
            <div className="mt-3">
              <ErrorMessage failure={error} />
            </div>
          ) : undefined,
        )}

        {settingCard(
          "Lemon Squeezy test mode",
          effectiveLsTestMode !== null ? (
            <Badge tone={effectiveLsTestMode ? "warning" : "success"} size="sm" dot>
              {effectiveLsTestMode ? "TEST" : "PROD"}
            </Badge>
          ) : null,
          effectiveLsTestMode
            ? "Test API key and sandbox variants; webhooks come from the Lemon Squeezy sandbox."
            : "Production API key and live variants: real payments are processed.",
          <Toggle enabled={effectiveLsTestMode ?? false} disabled={lsToggling || lsTestMode === null} onToggle={() => setConfirm("ls")} label="Lemon Squeezy test mode" risky />,
          lsError ? (
            <div className="mt-3">
              <ErrorMessage failure={lsError} />
            </div>
          ) : undefined,
        )}

        {settingCard(
          "Reconcile monetization summary",
          null,
          <>
            Rebuilds <code>_admin/monetizationSummary</code> from every user. It runs daily; force it to seed data or fix drift.
          </>,
          null,
          <div className="mt-3">
            <Button variant="outline" size="sm" onClick={() => setConfirm("reconcile")} loading={reconciling} className="min-w-36">
              Run reconciliation
            </Button>
            {reconcileMsg && <p className={reconcileMsg.startsWith("Already") ? "mt-2 text-caption text-muted-foreground" : "mt-2 text-caption text-success"}>{reconcileMsg}</p>}
            {reconcileError && (
              <div className="mt-2">
                <ErrorMessage failure={reconcileError} />
              </div>
            )}
          </div>,
        )}

        {settingCard(
          "Reconcile AI cost maps",
          null,
          <>
            Merges legacy dotted keys (<code>monthlyAiCost.YYYY-MM</code>) into nested maps. Dry-run first; missing months are never filled with $0.
          </>,
          null,
          <div className="mt-3">
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => void handleReconcileMaps(true)} loading={reconcilingMaps} className="min-w-20">
                Dry-run
              </Button>
              <Button variant="destructive" size="sm" onClick={() => setConfirm("maps")} disabled={reconcilingMaps}>
                Write nested maps
              </Button>
            </div>
            {mapsMsg && <p className="mt-2 text-caption text-success">{mapsMsg}</p>}
            {mapsError && (
              <div className="mt-2">
                <ErrorMessage failure={mapsError} />
              </div>
            )}
          </div>,
        )}
      </div>

      <ModelChangeDialog change={pendingChange} onCancel={() => setPendingChange(null)} onConfirm={applyModelChange} />
      <ConfirmDialog
        open={confirm === "ffa"}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={effectiveValue ? "Turn off free-for-all?" : "Turn on free-for-all?"}
        scope={
          effectiveValue
            ? "Plan limits apply again to every user right away."
            : "Every user gets PRO limits until you turn it off. Anthropic spend still stops at the Haiku daily cap: past it, evaluations and other background work wait for the next UTC day, so the queue grows instead of the bill. Plan to switch it off within 72 hours."
        }
        confirmLabel={effectiveValue ? "Turn off" : "Turn on for everyone"}
        destructive={!effectiveValue}
        onConfirm={handleToggle}
      />
      <ConfirmDialog
        open={confirm === "ls"}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={effectiveLsTestMode ? "Switch payments to production?" : "Switch payments to test mode?"}
        scope={
          effectiveLsTestMode
            ? "Checkouts use the live API key and variants: real cards are charged."
            : "Checkouts use the sandbox: no real payment goes through until you switch back."
        }
        confirmLabel={effectiveLsTestMode ? "Use production" : "Use test mode"}
        typeToConfirm={effectiveLsTestMode ? "PROD" : "TEST"}
        onConfirm={handleLsToggle}
      />
      <ConfirmDialog
        open={confirm === "reconcile"}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Rebuild the monetization summary?"
        scope="Scans every user and rewrites _admin/monetizationSummary."
        confirmLabel="Run reconciliation"
        destructive={false}
        onConfirm={handleReconcile}
      />
      <ConfirmDialog
        open={confirm === "maps"}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Write nested AI cost maps?"
        scope="Rewrites the token summary docs that still have dotted keys. Run the dry-run first to see how many."
        confirmLabel="Write maps"
        onConfirm={() => handleReconcileMaps(false)}
      />
    </div>
  );
}
